import { Component, Inject, LOCALE_ID } from '@angular/core';
import { Role } from '../../../model/auth/Role';
import { MobileAwareComponent } from '../../../common/mobile-aware/mobile-aware.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TenantService } from '../../../service/tenant.service';
import { Contest } from '../../../model/contest/Contest';
import { Observable } from 'rxjs';
import { Region } from '../../../model/location/Region';
import { Locale } from '../../../model/Locale';
import { LocationService } from '../../../service/location.service';
import { AuthService } from '../../../service/auth.service';
import { EnrolStats, StatsGroupingType } from '../../../model/stats/Stats';
import { NotificationHandler } from '../../../helper/notification.handler';
import { StatsService } from '../../../service/stats.service';
import { StatsHelper } from '../../../helper/stats.helper';

@Component({
  selector: 'app-enrol-stats',
  templateUrl: './enrol-stats.component.html',
  styleUrls: ['./enrol-stats.component.scss']
})
export class EnrolStatsComponent extends MobileAwareComponent {

  readonly roles = [Role.Host, Role.Coordinator];

  competitionId?: string;
  contest?: Contest;

  region?: Region;
  allowRegionSelect = false;

  paymentStats?: number[];
  paymentStatsLabels = StatsHelper.paymentStatusChartLabels();

  gradeStats?: number[];
  gradeStatsLabels = StatsHelper.gradeChartLabels();

  userStatusStats?: number[];
  userStatusStatsLabels = StatsHelper.userStatusChartLabels();

  schoolStats?: EnrolStats[];
  schoolStatsPageToken?: string;

  loadRegions = (): Observable<Region[]> => {
    return this.locationService.getRegions(this.locale as Locale);
  };

  constructor(private locationService: LocationService,
              private statsService: StatsService,
              private authService: AuthService,
              private tenantService: TenantService,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  async initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    const user = await this.authService.getUser();
    if (user && user.region) {
      this.region = new Region(user.region, '');
    }
  }

  compareRegions(r1?: Region, r2?: Region): boolean {
    return r1 != undefined && r2 != undefined && r1.id === r2.id;
  }

  displayRegion(region: Region): string {
    return region.name;
  }

  callerRolesUpdated(roles: Role[]) {
    if (roles.includes(Role.Host)) {
      this.allowRegionSelect = true;
    }
  }

  loadRegionStats(contest?: Contest, region?: Region) {
    if (!contest || !region) return;

    this.statsService.getEnrolStats(contest.id, region.id, StatsGroupingType.region).subscribe({
      next: stats => {
        const regionStats = stats && stats.content && stats.content.length ? stats.content[0] : new EnrolStats();

        this.paymentStats = StatsHelper.paymentStatusChartData(regionStats);
        this.gradeStats = StatsHelper.gradeChartData(regionStats);
        this.userStatusStats = StatsHelper.userStatusChartData(regionStats);
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
      }
    });
  }

  loadSchoolStats(contest?: Contest, region?: Region) {
    if (!contest || !region) return;

    const page = {
      max_items: 20,
      next_token: this.schoolStatsPageToken
    };
    this.statsService.getEnrolStats(contest.id, region.id, StatsGroupingType.school, page).subscribe(stats => {
      const data = stats?.content || [];

      if (data) {
        if (this.schoolStats) this.schoolStats.forEach(s => data.push(s));

        data.sort((s1, s2) => {
          return ((s2.status_successful || 0) + (s2.status_pending || 0) + (s2.status_scheduled || 0) + (s2.status_failed || 0))
            - ((s1.status_successful || 0) + (s1.status_pending || 0) + (s1.status_scheduled || 0) + (s1.status_failed || 0));
        });
      }

      this.schoolStats = data;
      this.schoolStatsPageToken = stats.next_token;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  regionSelected(region: Region) {
    this.region = region;

    this.schoolStats = undefined;
    this.schoolStatsPageToken = undefined;

    this.loadRegionStats(this.contest, this.region);
    this.loadSchoolStats(this.contest, this.region);
  }

  contestSelected(contest: Contest) {
    this.contest = contest;

    this.schoolStats = undefined;
    this.schoolStatsPageToken = undefined;

    this.loadRegionStats(this.contest, this.region);
    this.loadSchoolStats(this.contest, this.region);
  }
}
